import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PushBell } from "@/components/portal/PushBell";
import { GetAppButton } from "@/components/portal/GetAppButton";
import { DemoBar } from "@/components/portal/DemoBar";
import { I, Mark } from "@/components/portal/sys/icons";
import "@/styles/portal-sys.css";
import "@/styles/portal-extras.css";

/**
 * The workspace shell, translated element for element from `_mockups/v3`:
 * navy command rail with the Connexis lockup, the partner's own organisation card and
 * status pills, grouped navigation with count badges, the signed-in footer, then the
 * sticky breadcrumb bar and the page with its optional 320px right rail.
 *
 * Below 1024px the rail becomes a drawer, the breadcrumb bar becomes a compact top bar and
 * a bottom tab row appears — partners run these portals from a home-screen icon.
 */
const initialsOf = (s, n = 2) =>
  (s || "C").split(/\s+/).filter(Boolean).slice(0, n).map((w) => w[0]).join("").toUpperCase();

export function CrmShell({
  kind = "introducer",
  railLabel,
  orgName,
  orgSub,
  pills = [],
  groups = [],
  tabs = [],
  crumb,
  crumbRoot,
  topRight,
  rail,
  wide = false,
  children,
  testId,
}) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // "Message the desk" raises the floating widget in the partner portals, which answers this
  // event itself. The Hub has no widget on its pages, so there the same event opens the inbox.
  useEffect(() => {
    if (kind !== "hub") return;
    const go = () => nav("/hub/messages");
    window.addEventListener("portal-chat:open", go);
    return () => window.removeEventListener("portal-chat:open", go);
  }, [kind, nav]);

  const items = groups.flatMap((g) => g.items);
  const current = [...items].reverse().find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)))
    || [...items].reverse().find((n) => pathname.startsWith(`${n.to}/`));
  const here = crumb || current?.label || "";
  const root = crumbRoot || (kind === "lender" ? "Lender desk" : kind === "hub" ? "Hub" : "Introducer portal");
  const signOut = async () => {
    await logout();
    nav(kind === "lender" ? "/lender/login" : kind === "hub" ? "/login" : "/introducer/login", { replace: true });
  };

  return (
    <div className="cxp" data-testid={testId}>
      {user?.demo_session && <DemoBar />}
      <div className={`app${open ? " nav-open" : ""}`}>
        <aside className="side">
          <button className="side-close" onClick={() => setOpen(false)} aria-label="Close navigation" data-testid="portal-nav-close">
            <I.close />
          </button>

          <div className="brand">
            <div className="mark"><Mark /></div>
            <div>
              <div className="nm">Connexis<span className="bdot">.</span></div>
              <div className="sub2">{railLabel || root}</div>
            </div>
          </div>

          {orgName && (
            <div>
              <div className="org">
                <div className="logo">{initialsOf(orgName)}</div>
                <div>
                  <div className="t" data-testid="portal-org-name">{orgName}</div>
                  <div className="s">{orgSub}</div>
                </div>
              </div>
              {pills.length > 0 && (
                <div className="pills">
                  {pills.map((p) => (
                    <span key={p.label} className={`pill ${p.tone === "good" ? "pill-live" : "pill-admin"}`}
                          data-testid={`portal-pill-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {p.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {groups.map((g) => (
            <div key={g.title}>
              <div className="sect-lab">{g.title}</div>
              <nav className="nav">
                {g.items.map(({ to, label, icon, end, count, testId: tid }) => {
                  const Icon = I[icon] || I.grid;
                  return (
                    <NavLink key={to} to={to} end={end}
                             className={({ isActive }) => (isActive ? "on" : undefined)}
                             data-testid={tid || `portal-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <Icon />
                      {label}
                      {count > 0 && <span className="ct">{count}</span>}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
          ))}

          <div className="side-foot">
            <div className="av">{initialsOf(user?.name || user?.email, 1)}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="nm">{user?.name || "Signed in"}</div>
              <div className="em">{user?.email}</div>
            </div>
            <button onClick={signOut} aria-label="Sign out"
                    className="side-out"
                    data-testid={kind === "lender" ? "lender-logout" : "referrer-logout"}>
              <I.logout />
            </button>
          </div>
        </aside>

        <div className="main">
          <div className="m-bar">
            <button className="burger" onClick={() => setOpen(true)} aria-label="Open navigation" data-testid="portal-nav-toggle">
              <I.burger />
            </button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="eyebrow">{root}</div>
              <div className="ttl">{here}</div>
            </div>
            <GetAppButton compact />
            <PushBell />
          </div>

          <div className="topbar">
            <div className="crumb">{root}&nbsp;/&nbsp;<b>{here}</b></div>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
              {topRight}
              <GetAppButton />
              <PushBell />
            </div>
          </div>

          <div className="page" style={wide ? { maxWidth: 1620 } : undefined}>
            {rail ? (
              <div className="page-wide">
                <div style={{ minWidth: 0 }}>{children}</div>
                <aside className="rail">{rail}</aside>
              </div>
            ) : children}
          </div>
        </div>
      </div>

      {tabs.length > 0 && (
        <nav className="m-tabs" style={{ gridTemplateColumns: `repeat(${tabs.length},minmax(0,1fr))` }}
             data-testid="portal-bottom-nav">
          {tabs.map(({ to, label, icon, end }) => {
            const Icon = I[icon] || I.grid;
            return (
              <NavLink key={to} to={to} end={end} className={({ isActive }) => (isActive ? "on" : undefined)}
                       data-testid={`portal-tab-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Icon />
                {label}
              </NavLink>
            );
          })}
        </nav>
      )}
    </div>
  );
}

export default CrmShell;
